import * as THREE from 'three';
import type { Pickup } from '../entities/Pickup';

export type CircleCollider = {
  position: THREE.Vector3;
  radius: number;
  active?: boolean;
};

export class CollisionSystem {
  private readonly delta = new THREE.Vector3();

  overlapsXZ(aPosition: THREE.Vector3, aRadius: number, bPosition: THREE.Vector3, bRadius: number): boolean {
    const dx = aPosition.x - bPosition.x;
    const dz = aPosition.z - bPosition.z;
    const radius = aRadius + bRadius;
    return dx * dx + dz * dz <= radius * radius;
  }

  distanceSqXZ(a: THREE.Vector3, b: THREE.Vector3): number {
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    return dx * dx + dz * dz;
  }

  nearestWithin<T extends CircleCollider>(
    origin: THREE.Vector3,
    colliders: readonly T[],
    range: number,
  ): T | null {
    let nearest: T | null = null;
    let nearestDistanceSq = Number.POSITIVE_INFINITY;
    for (const collider of colliders) {
      if (collider.active === false) continue;
      const distanceSq = this.distanceSqXZ(origin, collider.position);
      const allowed = range + collider.radius;
      if (distanceSq <= allowed * allowed && distanceSq < nearestDistanceSq) {
        nearest = collider;
        nearestDistanceSq = distanceSq;
      }
    }
    return nearest;
  }

  segmentHitsCircleXZ(
    start: THREE.Vector3,
    end: THREE.Vector3,
    center: THREE.Vector3,
    radius: number,
  ): boolean {
    const segmentX = end.x - start.x;
    const segmentZ = end.z - start.z;
    const lengthSq = segmentX * segmentX + segmentZ * segmentZ;
    if (lengthSq <= Number.EPSILON) return this.distanceSqXZ(start, center) <= radius * radius;
    const t = THREE.MathUtils.clamp(
      ((center.x - start.x) * segmentX + (center.z - start.z) * segmentZ) / lengthSq,
      0,
      1,
    );
    const closestX = start.x + segmentX * t;
    const closestZ = start.z + segmentZ * t;
    const dx = center.x - closestX;
    const dz = center.z - closestZ;
    return dx * dx + dz * dz <= radius * radius;
  }

  collectPickups(playerPosition: THREE.Vector3, pickups: Pickup[], playerRadius: number): Pickup[] {
    const collected: Pickup[] = [];
    for (const pickup of pickups) {
      if (!pickup.active) continue;
      this.delta.copy(playerPosition).sub(pickup.group.position);
      this.delta.y = 0;
      const radius = playerRadius + pickup.radius;
      if (this.delta.lengthSq() <= radius * radius) {
        pickup.collect();
        collected.push(pickup);
      }
    }
    return collected;
  }
}
